import React, { useState } from "react";
import { fetchPost } from "../../../services/serviceClient";

interface CreateCaseStudyFormProps {
  isOpen: boolean;
  onClose: () => void;
  getAllCaseStudy: () => void;
}

const CreateCaseStudyForm: React.FC<CreateCaseStudyFormProps> = ({
  isOpen,
  onClose,
  getAllCaseStudy,
}) => {
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    featured_image: File | null;
    alt_text: string;
    category: string;
    upload_file: File | null;
    publish_date: string | null;
  }>({
    title: "",
    description: "",
    featured_image: null,
    alt_text: "",
    category: "",
    upload_file: null,
    publish_date: null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: file,
      }));
      if (errors[e.target.name]) {
        setErrors((prev) => ({
          ...prev,
          [e.target.name]: "",
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.featured_image) {
      newErrors.featured_image = "Featured image is required";
    } else if (!formData.featured_image?.type.startsWith("image/")) {
      newErrors.featured_image = "Please select a valid image file";
    }

    if (!formData.alt_text.trim()) {
      newErrors.alt_text = "Alt text for image is required";
    }

    if (!formData.category.trim()) {
      newErrors.category = "Category is required";
    }

    if (!formData.upload_file) {
      newErrors.upload_file = "Upload file is required";
    } else {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(formData.upload_file?.type || "")) {
        newErrors.upload_file = "Please upload a PDF or DOC file";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {

      // Reset form only after successful API call
      setFormData({
        title: "",
        description: "",
        featured_image: null,
        alt_text: "",
        category: "",
        upload_file: null,
        publish_date: null,
      });

      // Reset file inputs
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => ((input as HTMLInputElement).value = ""));

      // Close modal after successful submission
      onClose();
    } catch (error) {
      console.error("Error submitting case study:", error);
      alert("Error submitting case study. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnPublish = async () => {
    // Create FormData for file upload with correct field names for backend
    const formDataToSubmit = new FormData();
    formDataToSubmit.append("title", formData.title);
    formDataToSubmit.append("description", formData.description);
    formDataToSubmit.append("alt_text", formData.alt_text);
    formDataToSubmit.append("category", formData.category);
    formDataToSubmit.append("published_date", new Date().toISOString());
    
    // Use backend expected field names
    if (formData.featured_image) {
      formDataToSubmit.append("image", formData.featured_image); // Backend expects 'image'
    }
    if (formData.upload_file) {
      formDataToSubmit.append("file", formData.upload_file); // Backend expects 'file'
    }

    console.log("FormData being sent to API:");
    // Log FormData contents for debugging
    for (let pair of formDataToSubmit.entries()) {
      console.log(pair[0] + ': ' + pair[1]);
    }

    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/case-study/create-case-study`;
    const res = await fetchPost(url, formDataToSubmit);
    
    if (res.success) {
      getAllCaseStudy();
    } else {
      alert("Error publishing case study. Please try again.");
      throw new Error(res.error || "API call failed");
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      title: "",
      description: "",
      featured_image: null,
      alt_text: "",
      category: "",
      upload_file: null,
      publish_date: null,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="overflow-y-scroll border border-gray-300 rounded-lg fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[80vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Create New Case Study
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title Field */}
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.title ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter case study title (will be rendered as H1)"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.description ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter case study description (will be rendered as H2)"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          {/* Featured Image Field */}
          <div>
            <label
              htmlFor="featured_image"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Featured Image <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              id="featured_image"
              name="featured_image"
              onChange={handleFileChange}
              accept="image/*"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.featured_image ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.featured_image && (
              <p className="mt-1 text-sm text-red-500">
                {errors.featured_image}
              </p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Upload an image file (JPG, PNG, etc.)
            </p>
          </div>

          {/* Alt Text Field */}
          <div>
            <label
              htmlFor="alt_text"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Image Alt Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="alt_text"
              name="alt_text"
              value={formData.alt_text}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.alt_text ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter descriptive alt text for the image"
            />
            {errors.alt_text && (
              <p className="mt-1 text-sm text-red-500">{errors.alt_text}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Provide descriptive text for accessibility
            </p>
          </div>

          {/* Category Field */}
          <div>
            <label
              htmlFor="category"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Category <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.category ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="e.g., Fleet, CPOs, Enterprise"
            />
            {errors.category && (
              <p className="mt-1 text-sm text-red-500">{errors.category}</p>
            )}
          </div>

          {/* Upload File Field */}
          <div>
            <label
              htmlFor="upload_file"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Upload File (PDF/DOC) <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              id="upload_file"
              name="upload_file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx"
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.upload_file ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.upload_file && (
              <p className="mt-1 text-sm text-red-500">{errors.upload_file}</p>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-6 py-2 rounded-md font-medium text-white transition-colors ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              }`}
              onClick={handleOnPublish}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Publishing...
                </span>
              ) : (
                "Publish Case Study"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCaseStudyForm;
